import { useState } from "react";
import qs from "qs";
import { SearchState } from "react-instantsearch-core";
import { isSSR } from "src/utils/ssr";

export type UseMapQueryStringSearchReturnValues = {
  searchState: SearchState;
  triggerSearch: (updatedSearchState) => void;
  onSearchStateChange: (updatedSearchState, searchState) => void;
  createURL: (searchState) => string;
};

abstract class BaseQueryStringMapper {
  abstract pathname(): string;
  abstract search(): string;
  abstract push(any);

  createURL(searchState): string {
    return qs.stringify(searchState);
  }

  searchStateToUrl(searchState) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { configure, page, indices, ...filteredSearchState } = searchState;
    return searchState
      ? `${this.pathname()}?${this.createURL(filteredSearchState)}`
      : "";
  }

  fixWrongParams(searchState) {
    const refinementList = searchState["refinementList"];
    if (refinementList) {
      let departureYearMonth = refinementList["departureYearMonth"];
      if (departureYearMonth) {
        // Some email campaigns have a wrong value for departureYearMonth: hash instead of array
        if (!Array.isArray(departureYearMonth))
          departureYearMonth = Object.values(departureYearMonth);

        refinementList["departureYearMonth"] = departureYearMonth;
      }
    }
    if (searchState["q"]) {
      const query = searchState["q"];
      searchState["q"] = undefined;
      searchState["query"] = query;
    }
    return { ...searchState, refinementList };
  }

  urlToSearchState() {
    return this.fixWrongParams(qs.parse(this.search())) || {};
  }
}

class BrowserQueryStringMapper extends BaseQueryStringMapper {
  location?: Location;
  isSSR: boolean;
  constructor(isSSR) {
    super();
    this.isSSR = isSSR;
    this.location = isSSR ? undefined : window.location;
  }

  pathname() {
    return this.isSSR ? "" : this.location?.pathname || "";
  }

  search() {
    return this.isSSR ? "" : this.location?.search.slice(1) || "";
  }

  push(updatedSearchState) {
    if (!this.isSSR) {
      history.pushState(
        updatedSearchState,
        "",
        this.searchStateToUrl(updatedSearchState)
      );
    }
  }
}

export default function useMapQueryStringSearch(
  onSearchStateChange: (updatedSearchState, searchState) => void,
  updateQueryString = true
): UseMapQueryStringSearchReturnValues {
  const queryStringSearchMapper = new BrowserQueryStringMapper(isSSR());
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { page, ...searchParams } = queryStringSearchMapper.urlToSearchState();

  const [searchState, setSearchState] = useState(searchParams);
  const [debouncedSetState, setDebouncedSetState] = useState<
    NodeJS.Timeout | undefined
  >(undefined);

  const extendedOnSearchStateChange = (updatedSearchState, searchState) => {
    if (searchState == updatedSearchState) return;

    if (updateQueryString) {
      if (debouncedSetState) clearTimeout(debouncedSetState);

      setDebouncedSetState(
        setTimeout(() => {
          queryStringSearchMapper.push(updatedSearchState);
        }, 400)
      );
    }
    setSearchState(updatedSearchState);

    if (onSearchStateChange)
      onSearchStateChange(updatedSearchState, searchState);
  };

  const triggerSearch = (updatedSearchState) => {
    const mergedSearchState = { ...searchState, ...updatedSearchState };
    extendedOnSearchStateChange(mergedSearchState, searchState);
  };

  return {
    searchState,
    triggerSearch,
    onSearchStateChange: (updatedState) =>
      extendedOnSearchStateChange(updatedState, searchState),
    createURL: queryStringSearchMapper.createURL
  };
}
